import type { ComponentType } from 'react';
import memoizeOne from 'memoize-one';
import { ff } from '@atlassian/jira-feature-flagging';
import type {
	FieldType,
	CollectionType,
	CustomField,
	CustomFieldType,
} from '@atlassian/jira-forge-ui-types';
import type { CustomFieldProps } from '../types';
import Date from './date';
import DateTime from './date-time';
import { Group, Groups } from './group';
import Labels from './labels';
import SingleLineTextInline from './single-line-text-inline';
import { User, Users } from './user';
import { withRenderingMetrics } from './with-rendering-metrics';

export type FieldItemMap = {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	[C in CollectionType]?: Record<FieldType, ComponentType<CustomFieldProps<any>> | undefined>;
};

// TODO ARKEN-707: undefined components should receive an implementation
const getFieldMap = (): FieldItemMap => ({
	none: {
		string: SingleLineTextInline,
		number: SingleLineTextInline,
		object: SingleLineTextInline,
		user: User,
		group: Group,
		datetime: DateTime,
		date: Date,
	},
	list: {
		string: Labels,
		number: undefined,
		object: undefined,
		group: Groups,
		user: Users,
		datetime: undefined,
		date: undefined,
	},
});

const getComponent = (
	extension: CustomField | CustomFieldType | null, // eslint-disable-next-line @typescript-eslint/no-explicit-any
): ComponentType<CustomFieldProps<any>> | null => {
	if (!extension) {
		return null;
	}

	const { type, collection = 'none' } = extension.properties;
	const fieldCollection = getFieldMap()[collection];
	return (fieldCollection && fieldCollection[type]) ?? null;
};

const getComponentWithMetrics = (
	extension: CustomField | CustomFieldType | null, // eslint-disable-next-line @typescript-eslint/no-explicit-any
): ComponentType<CustomFieldProps<any>> | null => {
	const Component = getComponent(extension);
	if (!Component || !extension) return null;

	return withRenderingMetrics(Component, extension.id);
};

const memoizedGetComponentWithMetrics = memoizeOne(getComponentWithMetrics);

const getForgeCustomFields = (extension: CustomField | CustomFieldType | null) => {
	if (ff('remove-invalid-forge-metrics_n3ddj')) {
		return getComponent(extension);
	}
	const isCustomNativeField = !extension?.properties.function;
	if (isCustomNativeField) {
		return memoizedGetComponentWithMetrics(extension);
	}
	return getComponent(extension);
};

export default memoizeOne(getForgeCustomFields);
